// Dedicated Optum ticket detail page — ports the Claude Design "ticket-update"
// screen. Renders the read-back (status strip, facts, disputed lines,
// attachments, activity timeline), a Refresh Status button (POST …/refresh/),
// and the Update flow (ticketAction="Update") gated on allowedAction + isClosed.
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getReconsiderationDraft,
  refreshReconsiderationStatus,
  submitTicketUpdate,
} from '../../../api/claimActionsApi';
import {
  Icon, Button, Chip, Banner, PageHeader, CardShell, FactRow, StripStat,
  Timeline, DisputedLines, AttachmentList, Field, Select, Textarea,
  SectionHeader, SectionBody, Modal, fmtMoney, deriveUpdateChoices,
  canSubmitTicketUpdate, updateCtaMode,
} from './ui';

const REFRESH_POLL_MS = 2500;
const REFRESH_POLL_MAX = 6;
// Post-submit attachment-outcome polling (mirrors the refresh poll cadence).
const SUBMIT_POLL_MS = REFRESH_POLL_MS;
const SUBMIT_POLL_MAX = REFRESH_POLL_MAX;

// A post-submit poll is terminal once the ticket itself failed, or every
// attachment has reached a final upload_status (uploaded/failed).
function isSubmitTerminal(t) {
  if (!t) return false;
  if (t.status === 'submission_failed' || t.last_error_code) return true;
  const docs = t.documents || [];
  return docs.length > 0 && docs.every((d) => d.upload_status === 'uploaded' || d.upload_status === 'failed');
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function chipFor(t) {
  const out = (t.ticket_outcome || '').toLowerCase();
  if (out.includes('overturn') || out.includes('approv')) return 'approved';
  if (out.includes('uphel') || out.includes('den')) return 'denied';
  const st = (t.ticket_status || '').toLowerCase();
  if (st.includes('info')) return 'pending';
  if (t.is_closed) return 'sent';
  return 'review';
}

function buildEvents(t) {
  const events = [];
  if (t.submitted_at) {
    const attCount = (t.documents || []).length;
    events.push({
      id: 'submit', title: `${t.ticket_type || 'RECON'} submitted`, date: fmtDate(t.submitted_at),
      body: t.provider_comments || null,
      meta: [
        `Reason: ${t.ticket_reason || t.request_reconsideration_reason || '—'}`,
        attCount ? `${attCount} attachment${attCount !== 1 ? 's' : ''}` : null,
      ].filter(Boolean).join(' · '),
    });
  }
  if (t.ticket_outcome) {
    const chip = chipFor(t);
    events.push({
      id: 'outcome', kind: 'outcome', tone: chip === 'approved' ? 'approved' : 'denied',
      date: fmtDate(t.ticket_last_updated_at || t.status_refreshed_at),
      title: `Decision — ${t.ticket_outcome}`,
    });
  }
  return events;
}

function docId(d) {
  return d.document_id || (d.document && (d.document.id || d.document)) || d.id;
}
function docName(d) {
  return d.original_filename || (d.document && d.document.original_filename) || d.filename || 'Document';
}

export default function TicketDetailPage() {
  const { claimId, ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshedLabel, setRefreshedLabel] = React.useState('');
  const [updating, setUpdating] = React.useState(false);
  const [flash, setFlash] = React.useState(null);

  // Tracks mount so the async post-submit poll never setState after unmount.
  const mountedRef = React.useRef(true);
  const autoRefreshTicketRef = React.useRef(null);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = React.useCallback(async () => {
    const data = await getReconsiderationDraft(ticketId);
    setTicket(data);
    return data;
  }, [ticketId]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    load()
      .catch((e) => active && setError(e?.response?.data?.detail || 'Could not load ticket.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [load]);

  const doRefresh = React.useCallback(async ({ silent = false } = {}) => {
    if (!ticket) return;
    setRefreshing(true);
    setRefreshedLabel('');
    const before = ticket.status_refreshed_at || '';
    try {
      await refreshReconsiderationStatus(ticketId);
      // Worker is async — poll the detail endpoint until the refresh timestamp advances.
      for (let i = 0; i < REFRESH_POLL_MAX; i += 1) {
        await new Promise((r) => setTimeout(r, REFRESH_POLL_MS));
        const data = await load();
        if ((data.status_refreshed_at || '') !== before) {
          setRefreshedLabel('refreshed just now');
          break;
        }
      }
    } catch (e) {
      if (!silent) {
        setFlash({ kind: 'danger', title: 'Refresh failed', body: e?.response?.data?.detail || 'Try again in a moment.' });
      }
    } finally {
      setRefreshing(false);
    }
  }, [load, ticket, ticketId]);

  React.useEffect(() => {
    if (!ticket) return;
    const needsAllowedAction = ticket.ticket_number && !ticket.is_closed && !(ticket.allowed_action || '').trim();
    if (!needsAllowedAction || autoRefreshTicketRef.current === ticket.id) return;
    autoRefreshTicketRef.current = ticket.id;
    doRefresh({ silent: true });
  }, [doRefresh, ticket]);

  const handleUpdateSubmit = async (payload) => {
    await submitTicketUpdate(ticketId, payload);
    setUpdating(false);
    // Worker uploads attachments + submits async. Show a submitting flash and
    // poll the detail endpoint (keyed by ticketId via load) until terminal —
    // mirrors doRefresh; guarded by mountedRef so it never setState post-unmount.
    setFlash({ kind: 'info', title: 'Submitting update…', body: `Update queued on ticket ${ticket.ticket_number}. Tracking attachment upload…` });
    window.scrollTo({ top: 0, behavior: 'smooth' });

    let latest = null;
    for (let i = 0; i < SUBMIT_POLL_MAX; i += 1) {
      let data = null;
      try {
        data = await load();
      } catch (e) {
        data = null; // transient read error — keep polling
      }
      if (!mountedRef.current) return; // unmounted — stop, no further setState
      if (data) latest = data;
      if (isSubmitTerminal(data)) break;
      if (i < SUBMIT_POLL_MAX - 1) {
        await new Promise((r) => setTimeout(r, SUBMIT_POLL_MS));
      }
    }
    if (!mountedRef.current) return;

    const docs = (latest && latest.documents) || [];
    const anyFailed = docs.some((d) => d.upload_status === 'failed');
    const ticketFailed = !!(latest && (latest.status === 'submission_failed' || latest.last_error_code));
    if (ticketFailed || anyFailed) {
      setFlash({
        kind: 'danger',
        title: 'Update needs attention',
        body: ticketFailed
          ? 'The update could not be submitted. See the failure banner and attachment statuses below.'
          : 'One or more attachments failed to upload. See the attachment statuses below.',
      });
    } else {
      setFlash({ kind: 'success', title: 'Update submitted', body: `Update sent on ticket ${ticket.ticket_number}. All attachments uploaded.` });
    }
  };

  if (loading) {
    return <div style={{ fontFamily: 'Inter', padding: 40, color: '#64748B' }}>Loading ticket…</div>;
  }
  if (error || !ticket) {
    return (
      <div style={{ fontFamily: 'Inter', padding: 40 }}>
        <Banner kind="danger" title="Ticket unavailable" body={error || 'Not found.'} />
        <Button kind="secondary" icon="arrow-left" onClick={() => navigate(`/claims/${claimId}`)}>Back to Claim</Button>
      </div>
    );
  }

  if (updating) {
    return (
      <div className="ab" style={{ fontFamily: 'Inter', maxWidth: 980, margin: '0 auto', padding: '8px 4px 60px' }}>
        <UpdateTicketView ticket={ticket} onCancel={() => setUpdating(false)} onSubmit={handleUpdateSubmit} />
      </div>
    );
  }

  const canUpdate = canSubmitTicketUpdate(ticket);
  const lines = ticket.lines || [];
  const disputeTotal = lines.reduce((s, l) => s + (Number(l.line_amount_owed) || 0), 0);
  const events = buildEvents(ticket);
  const pending = ticket.is_closed
    ? null
    : { title: ticket.ticket_status || 'Under Review', body: 'Awaiting payer decision. Updates are accepted while the ticket is open.' };

  return (
    <div className="ab" style={{ fontFamily: 'Inter', maxWidth: 980, margin: '0 auto', padding: '8px 4px 60px' }}>
      <PageHeader
        title={ticket.ticket_number || 'Reconsideration Ticket'}
        subtitle={`${ticket.ticket_type || 'RECON'} reconsideration${ticket.ticket_number ? '' : ' · not yet submitted to Optum'}`}
      >
        <Button kind="ghost" icon="arrow-left" onClick={() => navigate(`/claims/${claimId}`)}>Back to Claim</Button>
        <Button kind="secondary" icon="refresh-cw" onClick={doRefresh} disabled={refreshing || !ticket.ticket_number}>
          {refreshing ? 'Refreshing…' : 'Refresh Status'}
        </Button>
        <UpdateButton
          canUpdate={canUpdate}
          ticket={ticket}
          onClick={() => { setFlash(null); setUpdating(true); }}
          onStartNew={() => navigate(`/claims/${claimId}/appeal`)}
        />
      </PageHeader>

      {flash && <Banner kind={flash.kind} title={flash.title} body={flash.body} onDismiss={() => setFlash(null)} />}
      {ticket.status === 'submission_failed' && ticket.last_error_message && (
        <Banner kind="danger" title="Last submission failed" body={ticket.last_error_message} />
      )}

      {/* Status strip */}
      <div style={{ border: '1px solid #000', background: '#fff', padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 28, rowGap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Chip status={chipFor(ticket)}>{ticket.ticket_outcome || ticket.ticket_status || 'Draft'}</Chip>
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: '#64748B' }}>
            {ticket.is_closed ? 'Closed' : 'Open'}
            {refreshedLabel ? <span style={{ color: '#1F8A4C', marginLeft: 8 }}>· {refreshedLabel}</span> : null}
          </div>
        </div>
        <div style={{ height: 28, width: 1, background: '#E5E7EB' }} />
        <StripStat label="Allowed Action" value={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <code style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13, fontWeight: 700 }}>{ticket.allowed_action || '—'}</code>
            {canUpdate
              ? <Icon name="unlock" size={13} style={{ color: '#1F8A4C' }} />
              : <Icon name="lock" size={13} style={{ color: '#94A3B8' }} />}
          </span>
        } />
        <StripStat label="Reason" value={ticket.ticket_reason || ticket.request_reconsideration_reason || '—'} />
        <StripStat label="Submitted" value={fmtDate(ticket.submitted_at)} />
        <StripStat label="Last Updated" value={fmtDate(ticket.ticket_last_updated_at || ticket.status_refreshed_at)} />
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>In Dispute</div>
          <div style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(disputeTotal)}</div>
        </div>
      </div>

      {(ticket.ticket_status || '').toLowerCase().includes('info') && canUpdate && (
        <Banner
          kind="warn"
          title="The payer requested more information"
          body="Respond with the requested records as an update — it attaches to this ticket and keeps the reconsideration open."
          action="Create Update"
          onAction={() => setUpdating(true)}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 20, alignItems: 'start' }}>
        <CardShell title="Activity" right={<span style={{ fontFamily: 'Inter', fontSize: 11, color: '#64748B' }}>{events.length} event{events.length !== 1 ? 's' : ''}</span>}>
          {events.length ? <Timeline events={events} pending={pending} /> : <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B' }}>No activity yet.</div>}
        </CardShell>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <CardShell title="Ticket">
            <FactRow label="Ticket #" mono>{ticket.ticket_number || '—'}</FactRow>
            <FactRow label="Type">{ticket.ticket_type || '—'}</FactRow>
            <FactRow label="Status">{ticket.ticket_status || ticket.status || '—'}</FactRow>
            <FactRow label="Outcome">{ticket.ticket_outcome || '—'}</FactRow>
            <FactRow label="Reason">{ticket.ticket_reason || ticket.request_reconsideration_reason || '—'}</FactRow>
            <FactRow label="Closed">{ticket.is_closed ? 'Yes' : 'No'}</FactRow>
          </CardShell>

          <CardShell title="Disputed Lines">
            {lines.length ? <DisputedLines lines={lines} /> : <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B' }}>No disputed lines recorded.</div>}
          </CardShell>

          <CardShell title={`Attachments · ${(ticket.documents || []).length}`}>
            <AttachmentList attachments={ticket.documents || []} />
          </CardShell>
        </div>
      </div>
    </div>
  );
}

// Update CTA — branches on updateCtaMode (ports the design's four states).
function UpdateButton({ canUpdate, ticket, onClick, onStartNew }) {
  const [hover, setHover] = React.useState(false);
  const mode = updateCtaMode(ticket, canUpdate);
  if (mode === 'update') {
    return <Button kind="primary" icon="plus" onClick={onClick}>Create Update</Button>;
  }
  if (mode === 'start-new') {
    return <Button kind="primary" icon="file-plus" onClick={onStartNew}>Start New Reconsideration</Button>;
  }
  if (mode === 'resolved') {
    return <Button kind="secondary" icon="check" disabled style={{ opacity: 0.6, cursor: 'default' }}>Resolved — Overturned</Button>;
  }
  const why = ticket.is_closed
    ? `This ticket is ${ticket.ticket_outcome ? `closed (${ticket.ticket_outcome})` : 'closed'}. Optum no longer accepts updates — start a new reconsideration if needed.`
    : `allowedAction is ${ticket.allowed_action || '—'}. Updates open once Optum returns an open ticket with allowedAction = "Update" or "View".`;
  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <Button kind="secondary" icon="lock" disabled style={{ opacity: 0.5 }}>Update Unavailable</Button>
      {hover && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, width: 260, background: '#000', color: '#fff', fontFamily: 'Inter', fontSize: 12, lineHeight: 1.5, padding: '10px 12px', zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
          {why}
        </div>
      )}
    </div>
  );
}

// ── Update Ticket sub-view ──────────────────────────────────────────────────
function UpdateTicketView({ ticket, onCancel, onSubmit }) {
  const { reasonChoices, docTypeChoices } = deriveUpdateChoices(ticket);

  // reasonChoices are { value, label } — newReason holds the VALUE (code) that is
  // submitted; the Select renders the label. Default to the ticket's current reason
  // value, falling back to the first choice's value.
  const currentReason =
    ticket.ticket_reason || ticket.request_reconsideration_reason || (reasonChoices[0] && reasonChoices[0].value) || '';
  const reasonOptions = reasonChoices.some((o) => o.value === currentReason)
    ? reasonChoices
    : [{ value: currentReason, label: currentReason }, ...reasonChoices].filter((o) => o.value);
  const [newReason, setNewReason] = React.useState(currentReason);
  const [comment, setComment] = React.useState('');
  const [newDocs, setNewDocs] = React.useState([]); // { id, name, code }
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const available = ticket.available_documents || [];
  const reasonChanged = newReason !== currentReason;
  const valid = comment.trim().length > 0 && !submitting;
  const disputeTotal = (ticket.lines || []).reduce((s, l) => s + (Number(l.line_amount_owed) || 0), 0);

  const addDoc = (d) => {
    const id = docId(d);
    if (newDocs.find((x) => x.id === id)) return;
    setNewDocs((arr) => [...arr, { id, name: docName(d), code: 'M1' }]);
  };
  const removeDoc = (id) => setNewDocs((arr) => arr.filter((x) => x.id !== id));
  const setDocCode = (id, code) => setNewDocs((arr) => arr.map((x) => (x.id === id ? { ...x, code } : x)));

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({
        request_reconsideration_reason: newReason,
        provider_comments: comment.trim(),
        documents: newDocs.map((d) => ({ document_id: d.id, document_type_code: d.code })),
      });
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={`Update ${ticket.ticket_number}`}
        subtitle={'ticketAction = "Update" · sent against the existing ticket — no new claim action is created'}
      >
        <Button kind="ghost" icon="arrow-left" onClick={onCancel}>Back to Ticket</Button>
      </PageHeader>

      <Banner
        kind="info"
        title="This updates the existing ticket"
        body="Optum returned allowedAction = Update for this claim. Your comment, any reason change, and new attachments are submitted with this ticketNumber — attachments upload against the ticket, not a new claimActionIdentifier."
      />

      <SectionHeader step={1} title="Reconsideration Reason" subtitle="Pulled from this payer’s claim-inquiry response (allowedReconReasons)." complete />
      <SectionBody>
        <div style={{ maxWidth: 520 }}>
          <Field label="requestReconsiderationReason">
            <Select value={newReason} options={reasonOptions} onChange={setNewReason} />
          </Field>
          {reasonChanged ? (
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: '#B7791F', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="corner-down-right" size={13} /> Changing from <strong style={{ color: '#000' }}>{currentReason}</strong>
            </div>
          ) : (
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: '#64748B', marginTop: 8 }}>
              Current reason — leave as-is unless the basis for the dispute has changed.
            </div>
          )}
        </div>
      </SectionBody>

      <SectionHeader step={2} title="Comment to the Reviewer" subtitle="Added to the ticket’s comment thread (providerComments)." complete={comment.trim().length > 0} />
      <SectionBody>
        <Field label="Your message" hint="Add context, respond to a reviewer note, or note newly attached evidence.">
          <Textarea rows={4} value={comment} placeholder="e.g. Attaching the signed plan of care and 01/14 visit notes supporting the units billed, as requested." onChange={(e) => setComment(e.target.value)} />
        </Field>
      </SectionBody>

      <SectionHeader step={3} title="Additional Documentation" subtitle={`${newDocs.length} new attachment${newDocs.length !== 1 ? 's' : ''} · uploaded via ticketNumber`} complete={newDocs.length > 0} />
      <SectionBody>
        {newDocs.length === 0 && (
          <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B', marginBottom: 12 }}>
            Optional — attach records to strengthen the update. Each upload carries an Optum reportTypeCode.
          </div>
        )}
        {newDocs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {newDocs.map((d) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #000', background: '#F8F9FC', padding: '10px 12px' }}>
                <Icon name="file-text" size={16} />
                <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
                <select value={d.code} onChange={(e) => setDocCode(d.id, e.target.value)} style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, padding: '5px 8px', border: '1px solid #000', background: '#fff', cursor: 'pointer' }}>
                  {docTypeChoices.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.desc}</option>)}
                </select>
                <span onClick={() => removeDoc(d.id)} style={{ cursor: 'pointer', color: '#64748B', display: 'inline-flex' }}><Icon name="x" size={15} /></span>
              </div>
            ))}
          </div>
        )}
        <Button kind="secondary" icon="folder" onClick={() => setPickerOpen(true)} disabled={available.length === 0}>
          {available.length ? 'Choose from Library' : 'No attached documents'}
        </Button>
      </SectionBody>

      <SectionHeader step={4} title="Review & Submit" complete={valid} />
      <SectionBody style={{ paddingBottom: 40 }}>
        <div style={{ background: '#000', color: '#fff', padding: 20, fontFamily: 'Inter' }}>
          <div style={{ fontSize: 11, color: '#A0ABBB', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 14 }}>
            submitClaimTicket — payload preview
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px' }}>
            <PayloadKV label="ticketAction" value="Update" mono accent />
            <PayloadKV label="ticketNumber" value={ticket.ticket_number} mono />
            <PayloadKV label="ticketType" value={ticket.ticket_type} mono />
            <PayloadKV label="requestReconsiderationReason" value={newReason} accent={reasonChanged} />
            <PayloadKV label="providerComments" value={comment.trim() ? `${comment.trim().length} chars` : '—'} />
            <PayloadKV label="attachments (via ticketNumber)" value={newDocs.length ? `${newDocs.length} new` : 'none'} />
            <PayloadKV label="claimAmountOwed" value={fmtMoney(disputeTotal)} mono />
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #1D2433', fontSize: 11, color: '#A0ABBB', lineHeight: 1.5 }}>
            Line details are unchanged from the original ticket. ticketNumber is included (required for updates); no new claimActionIdentifier is sent.
          </div>
        </div>

        {!valid && !submitting && (
          <div style={{ marginTop: 14 }}>
            <Banner kind="warn" title="Add a comment to continue" body="Optum updates carry a provider comment describing the change. Add a short message to the reviewer above." />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button kind="ghost" onClick={onCancel}>Cancel</Button>
          <Button kind="primary" icon="send" onClick={submit} disabled={!valid} style={{ opacity: valid ? 1 : 0.5 }}>
            {submitting ? 'Submitting…' : 'Submit Update'}
          </Button>
        </div>
      </SectionBody>

      <Modal open={pickerOpen} title="Document Library" onClose={() => setPickerOpen(false)}
        footer={<Button kind="primary" onClick={() => setPickerOpen(false)}>Done</Button>}>
        <div style={{ fontFamily: 'Inter', fontSize: 13, color: '#64748B', marginBottom: 12 }}>
          Select records to upload against this ticket.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflow: 'auto' }}>
          {available.map((d) => {
            const id = docId(d);
            const added = newDocs.find((x) => x.id === id);
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #E5E7EB', background: added ? '#F8F9FC' : '#fff' }}>
                <Icon name="file-text" size={16} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{docName(d)}</div>
                </div>
                {added ? (
                  <span style={{ fontFamily: 'Inter', fontSize: 12, color: '#1F8A4C', fontWeight: 600 }}>Added</span>
                ) : (
                  <Button kind="secondary" size="sm" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => addDoc(d)}>Add</Button>
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}

function PayloadKV({ label, value, mono, accent }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#A0ABBB', fontFamily: 'Inter' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, marginTop: 3, fontFamily: mono ? 'ui-monospace, "SF Mono", Menlo, monospace' : 'Inter', color: accent ? '#FBBF24' : '#fff', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}
